import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { listVendorDirectoryVendors } from "../../api/clientVendorDirectoryApi";
import { useVendorDirectoryPermissions } from "../../hooks/useVendorDirectoryPermissions";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import { casamapaPublicVendorDirectoryHref } from "../../constants/casamapaVendorWorkspacePaths";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.vendors";

const LIFECYCLE_FILTERS = [
  "",
  "discovered",
  "normalized",
  "pending_verification",
  "contact_attempted",
  "active",
  "suspended",
  "inactive",
  "archived",
];

const PUBLICATION_FILTERS = ["", "draft", "ready_for_review", "published", "unpublished", "suppressed"];

const QUICK_FILTERS = [
  { id: "all", lifecycleStatus: "", publicationStatus: "" },
  { id: "draft", lifecycleStatus: "", publicationStatus: "draft" },
  { id: "published", lifecycleStatus: "", publicationStatus: "published" },
  { id: "pendingVerification", lifecycleStatus: "pending_verification", publicationStatus: "" },
  { id: "active", lifecycleStatus: "active", publicationStatus: "" },
];

function publicationBadgeClass(status) {
  if (status === "published") return "cvd-badge--published";
  if (status === "suppressed" || status === "unpublished") return "cvd-badge--suspended";
  if (status === "ready_for_review") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function lifecycleBadgeClass(status) {
  if (status === "active") return "cvd-badge--published";
  if (["suspended", "archived", "inactive"].includes(status)) return "cvd-badge--suspended";
  if (status === "pending_verification") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function VendorInitialsMark({ name }) {
  const initials = (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  return (
    <span className="cvd-vendor-list__mark" aria-hidden="true">
      {initials || "?"}
    </span>
  );
}

function CompletenessBar({ value }) {
  const pct = Math.min(100, Math.max(0, Math.round(value || 0)));
  return (
    <div className="cvd-vendor-completeness cvd-vendor-list__completeness" aria-label={`${pct}%`}>
      <div className="cvd-vendor-completeness__track">
        <div className="cvd-vendor-completeness__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="cvd-vendor-completeness__label">{pct}%</span>
    </div>
  );
}

function VendorListRow({ vendor, basePath, t, locale }) {
  const completeness = Math.round(vendor.profileCompletenessScore || 0);

  return (
    <Link className="cvd-vendor-list__row" to={`${basePath}/${vendor.id}`}>
      <VendorInitialsMark name={vendor.displayName} />
      <div className="cvd-vendor-list__row-main">
        <div className="cvd-vendor-list__row-head">
          <h2 className="cvd-vendor-list__name">{vendor.displayName}</h2>
          <div className="cvd-vendor-list__badges">
            <span className={`cvd-badge ${lifecycleBadgeClass(vendor.lifecycleStatus)}`}>
              {t(`${COPY_PREFIX}.lifecycle.${vendor.lifecycleStatus}`, {}, vendor.lifecycleStatus)}
            </span>
            <span className={`cvd-badge ${publicationBadgeClass(vendor.publicationStatus)}`}>
              {t(`${COPY_PREFIX}.publication.${vendor.publicationStatus}`, {}, vendor.publicationStatus)}
            </span>
          </div>
        </div>
        <p className="cvd-vendor-list__meta">
          {vendor.publicId || vendor.slug || "—"}
          {vendor.primaryCategoryName ? ` · ${vendor.primaryCategoryName}` : ""}
          {vendor.primaryMunicipalityName ? ` · ${vendor.primaryMunicipalityName}` : ""}
        </p>
        <div className="cvd-vendor-list__row-foot">
          <span className="cvd-vendor-list__updated">
            {t(`${COPY_PREFIX}.table.updated`)}: {formatDateTime(vendor.updatedAt, locale)}
          </span>
          <span className="cvd-vendor-list__completeness-label">
            {t(`${COPY_PREFIX}.table.completeness`)}: {completeness}%
          </span>
        </div>
      </div>
      <div className="cvd-vendor-list__row-aside">
        <CompletenessBar value={vendor.profileCompletenessScore} />
        <span className="cvd-vendor-list__chevron" aria-hidden="true">
          →
        </span>
      </div>
    </Link>
  );
}

function FiltersPanel({ lifecycleStatus, publicationStatus, includeArchived, onChange, onClear, t }) {
  return (
    <div className="cvd-vendor-list__filters-panel">
      <div className="cvd-vendor-list__filters-grid">
        <label className="cvd-field">
          <span>{t(`${COPY_PREFIX}.filterLifecycle`)}</span>
          <select
            value={lifecycleStatus}
            onChange={(event) => onChange("lifecycleStatus", event.target.value)}
          >
            {LIFECYCLE_FILTERS.map((value) => (
              <option key={value || "all-lifecycle"} value={value}>
                {value ? t(`${COPY_PREFIX}.lifecycle.${value}`) : t(`${COPY_PREFIX}.allLifecycle`)}
              </option>
            ))}
          </select>
        </label>

        <label className="cvd-field">
          <span>{t(`${COPY_PREFIX}.filterPublication`)}</span>
          <select
            value={publicationStatus}
            onChange={(event) => onChange("publicationStatus", event.target.value)}
          >
            {PUBLICATION_FILTERS.map((value) => (
              <option key={value || "all-publication"} value={value}>
                {value ? t(`${COPY_PREFIX}.publication.${value}`) : t(`${COPY_PREFIX}.allPublication`)}
              </option>
            ))}
          </select>
        </label>

        <label className="cvd-vendor-list__checkbox">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => onChange("includeArchived", event.target.checked ? "true" : "")}
          />
          {t(`${COPY_PREFIX}.filterIncludeArchived`)}
        </label>
      </div>

      <div className="cvd-vendor-list__filters-actions">
        <button type="button" className="cvd-btn cvd-btn--ghost" onClick={onClear}>
          {t(`${COPY_PREFIX}.clearFilters`)}
        </button>
      </div>
    </div>
  );
}

export default function ClientVendorDirectoryListPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { permissions } = useVendorDirectoryPermissions();
  const { t, locale } = useLocale();
  const publicDirectoryHref = casamapaPublicVendorDirectoryHref(locale);
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(searchParams.get("search") || "");
  const searchDebounceRef = useRef(null);

  const search = searchParams.get("search") || "";
  const lifecycleStatus = searchParams.get("lifecycleStatus") || "";
  const publicationStatus = searchParams.get("publicationStatus") || "";
  const includeArchived = searchParams.get("includeArchived") === "true";
  const page = Number(searchParams.get("page") || 1);
  const basePath = `${serviciosBase}/proveedores`;
  const viewName = activeView?.name || "CasaMapa";
  const canView = permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.VENDORS_VIEW);

  const activeQuickFilter = useMemo(() => {
    const match = QUICK_FILTERS.find(
      (preset) => preset.lifecycleStatus === lifecycleStatus && preset.publicationStatus === publicationStatus
    );
    return match?.id || null;
  }, [lifecycleStatus, publicationStatus]);

  const hasAdvancedFilters = Boolean(lifecycleStatus || publicationStatus || includeArchived || search);

  useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  useEffect(() => {
    if (hasAdvancedFilters && !activeQuickFilter) {
      setFiltersOpen(true);
    }
  }, [activeQuickFilter, hasAdvancedFilters]);

  const load = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      setData(null);
      return;
    }
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listVendorDirectoryVendors(clientViewId, {
        page,
        pageSize: 25,
        search: search || undefined,
        lifecycleStatus: lifecycleStatus || undefined,
        publicationStatus: publicationStatus || undefined,
        sort: searchParams.get("sort") || "updated_at",
        sortDir: searchParams.get("sortDir") || "desc",
        includeArchived: includeArchived || undefined,
      });
      setData(body);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [canView, clientViewId, includeArchived, lifecycleStatus, page, publicationStatus, search, searchParams, t]);

  useEffect(() => {
    load();
  }, [load]);

  const vendors = data?.vendors || [];
  const pagination = data?.pagination || { page: 1, totalPages: 0, total: 0 };

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const applyQuickFilter = (preset) => {
    const next = new URLSearchParams(searchParams);
    if (preset.lifecycleStatus) next.set("lifecycleStatus", preset.lifecycleStatus);
    else next.delete("lifecycleStatus");
    if (preset.publicationStatus) next.set("publicationStatus", preset.publicationStatus);
    else next.delete("publicationStatus");
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("search");
    next.delete("lifecycleStatus");
    next.delete("publicationStatus");
    next.delete("includeArchived");
    next.delete("page");
    setSearchParams(next, { replace: true });
    setSearchDraft("");
    setFiltersOpen(false);
  };

  const setPage = (nextPage) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage > 1) next.set("page", String(nextPage));
    else next.delete("page");
    setSearchParams(next);
  };

  const handleSearchChange = (value) => {
    setSearchDraft(value);
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = window.setTimeout(() => {
      updateParam("search", value.trim());
    }, 300);
  };

  if (!canView) {
    return (
      <div className="page-content cvd-admin cvd-vendors">
        <section className="cvd-run-empty">
          <p role="alert">{t(`${COPY_PREFIX}.noPermission`)}</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page-content cvd-admin cvd-vendors cvd-vendor-list">
      <header className="cvd-vendor-list__header">
        <div className="cvd-vendor-list__header-copy">
          <p className="cvd-vendor-list__eyebrow">{t(`${COPY_PREFIX}.eyebrow`, { viewName })}</p>
          <h1 className="cvd-vendor-list__title">{t(`${COPY_PREFIX}.title`)}</h1>
          <p className="cvd-vendor-list__intro">{t(`${COPY_PREFIX}.intro`, { viewName })}</p>
        </div>
        <div className="cvd-vendor-list__header-actions">
          <a className="cvd-btn cvd-btn--ghost" href={publicDirectoryHref} target="_blank" rel="noreferrer">
            {t(`${COPY_PREFIX}.viewPublicDirectory`)}
          </a>
          <button type="button" className="cvd-btn cvd-btn--ghost" onClick={load}>
            {t(`${COPY_PREFIX}.refresh`)}
          </button>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.VENDORS_CREATE}>
            <Link className="cvd-btn cvd-btn--primary" to={`${basePath}/nuevo`}>
              {t(`${COPY_PREFIX}.addToPublicSite`)}
            </Link>
          </VendorDirectoryPermissionGate>
        </div>
      </header>

      <section className="cvd-public-path" aria-label={t(`${COPY_PREFIX}.publicPathTitle`)}>
        <div>
          <h2>{t(`${COPY_PREFIX}.publicPathTitle`)}</h2>
          <p>{t(`${COPY_PREFIX}.publicPathIntro`)}</p>
        </div>
        <ol>
          <li>{t(`${COPY_PREFIX}.publicPathSteps.add`)}</li>
          <li>{t(`${COPY_PREFIX}.publicPathSteps.complete`)}</li>
          <li>{t(`${COPY_PREFIX}.publicPathSteps.publish`)}</li>
        </ol>
      </section>

      <section className="cvd-vendor-list__toolbar" aria-label={t(`${COPY_PREFIX}.searchLabel`)}>
        <label className="cvd-vendor-list__search">
          <span className="sr-only">{t(`${COPY_PREFIX}.searchLabel`)}</span>
          <input
            type="search"
            value={searchDraft}
            onChange={(event) => handleSearchChange(event.target.value)}
            placeholder={t(`${COPY_PREFIX}.searchPlaceholder`)}
          />
        </label>
        <button
          type="button"
          className={`cvd-btn cvd-btn--secondary${filtersOpen ? " is-active" : ""}`}
          onClick={() => setFiltersOpen((current) => !current)}
          aria-expanded={filtersOpen}
        >
          {t(`${COPY_PREFIX}.filtersToggle`)}
          {hasAdvancedFilters ? <span className="cvd-vendor-list__filter-dot" aria-hidden="true" /> : null}
        </button>
      </section>

      <div className="cvd-vendor-list__quick-filters" role="group" aria-label={t(`${COPY_PREFIX}.quickFiltersLabel`)}>
        {QUICK_FILTERS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className={`cvd-vendor-list__quick-filter${activeQuickFilter === preset.id ? " is-active" : ""}`}
            onClick={() => applyQuickFilter(preset)}
          >
            {t(`${COPY_PREFIX}.quickFilters.${preset.id}`)}
          </button>
        ))}
      </div>

      {filtersOpen ? (
        <FiltersPanel
          lifecycleStatus={lifecycleStatus}
          publicationStatus={publicationStatus}
          includeArchived={includeArchived}
          onChange={updateParam}
          onClear={clearFilters}
          t={t}
        />
      ) : null}

      <div className="cvd-vendor-list__summary">
        <p className="cvd-vendor-list__result-count">
          {t(`${COPY_PREFIX}.resultCount`, { count: pagination.total || vendors.length })}
        </p>
        {hasAdvancedFilters ? (
          <button type="button" className="cvd-vendor-list__clear-link" onClick={clearFilters}>
            {t(`${COPY_PREFIX}.clearFilters`)}
          </button>
        ) : null}
      </div>

      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {!loading && errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}

      {!loading && !errorMessage && vendors.length === 0 ? (
        <section className="cvd-vendor-list__empty">
          <h2>{t(`${COPY_PREFIX}.emptyTitle`)}</h2>
          <p>{t(`${COPY_PREFIX}.emptyHint`)}</p>
          {hasAdvancedFilters ? (
            <button type="button" className="cvd-btn cvd-btn--secondary" onClick={clearFilters}>
              {t(`${COPY_PREFIX}.clearFilters`)}
            </button>
          ) : (
            <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.VENDORS_CREATE}>
              <Link className="cvd-btn cvd-btn--primary" to={`${basePath}/nuevo`}>
                {t(`${COPY_PREFIX}.addToPublicSite`)}
              </Link>
            </VendorDirectoryPermissionGate>
          )}
        </section>
      ) : null}

      {!loading && !errorMessage && vendors.length > 0 ? (
        <div className="cvd-vendor-list__rows">
          {vendors.map((vendor) => (
            <VendorListRow key={vendor.id} vendor={vendor} basePath={basePath} t={t} locale={locale} />
          ))}
        </div>
      ) : null}

      {!loading && !errorMessage && pagination.totalPages > 1 ? (
        <nav className="cvd-vendor-list__pagination" aria-label={t(`${COPY_PREFIX}.page`, { page, totalPages: pagination.totalPages })}>
          <button
            type="button"
            className="cvd-btn cvd-btn--ghost"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            {t(`${COPY_PREFIX}.prevPage`)}
          </button>
          <span className="muted">
            {t(`${COPY_PREFIX}.page`, { page, totalPages: pagination.totalPages })}
          </span>
          <button
            type="button"
            className="cvd-btn cvd-btn--ghost"
            disabled={page >= pagination.totalPages}
            onClick={() => setPage(page + 1)}
          >
            {t(`${COPY_PREFIX}.nextPage`)}
          </button>
        </nav>
      ) : null}
    </div>
  );
}
